import { Breadcrumb as AlbaBreadcrumb } from '@noorden-insurtech/alba-design-system';
import { Link } from '@tanstack/react-router';
import { ComponentProps, HTMLAttributes, ReactNode } from 'react';

type RootProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
};

function Root({ children, className = '', ...props }: RootProps) {
  return (
    <div
      className={`mx-auto flex h-full w-full min-w-0 flex-col gap-4 px-4 pt-0 pb-8 max-w-5xl lg:max-w-7xl ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}
Root.displayName = 'Page.Root';

type BreadcrumbProps = ComponentProps<typeof AlbaBreadcrumb>;

function Breadcrumb(props: BreadcrumbProps) {
  return (
    <AlbaBreadcrumb
      {...props}
      renderLink={(item, props) => <Link to={item.href} {...props} />}
    />
  );
}

Breadcrumb.displayName = 'Page.Breadcrumb';

type PageHeaderRootProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
};

function PageHeaderRoot({
  children,
  className = '',
  ...props
}: PageHeaderRootProps) {
  return (
    <div className={`flex flex-col gap-4 ${className}`} {...props}>
      {children}
    </div>
  );
}
PageHeaderRoot.displayName = 'PageHeader.Root';

type PageHeaderMainProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
};

function PageHeaderMain({
  children,
  className = '',
  ...props
}: PageHeaderMainProps) {
  return (
    <div
      className={`flex w-full flex-auto flex-col gap-4 md:flex-row md:items-center md:justify-between ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}
PageHeaderMain.displayName = 'PageHeader.Main';

type PageHeaderTitleGroupProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
};

function PageHeaderTitleGroup({
  children,
  className = '',
  ...props
}: PageHeaderTitleGroupProps) {
  return (
    <div className={`flex flex-col ${className}`} {...props}>
      {children}
    </div>
  );
}
PageHeaderTitleGroup.displayName = 'PageHeader.TitleGroup';

type PageHeaderTitleProps = HTMLAttributes<HTMLHeadingElement> & {
  children: ReactNode;
  className?: string;
};

function PageHeaderTitle({
  children,
  className = '',
  ...props
}: PageHeaderTitleProps) {
  return (
    <h1
      className={`text-[30px] font-normal leading-normal text-alba-black-500 font-montserrat col-start-1 ${className}`}
      {...props}
    >
      {children}
    </h1>
  );
}
PageHeaderTitle.displayName = 'PageHeader.Title';

type PageHeaderSubtitleProps = HTMLAttributes<HTMLParagraphElement> & {
  children: ReactNode;
  className?: string;
};

function PageHeaderSubtitle({
  children,
  className = '',
  ...props
}: PageHeaderSubtitleProps) {
  return (
    <p
      className={`text-sm font-normal leading-normal text-alba-gray-600 col-start-1 ${className}`}
      {...props}
    >
      {children}
    </p>
  );
}
PageHeaderSubtitle.displayName = 'PageHeader.Subtitle';

type PageHeaderActionsProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
};

function PageHeaderActions({
  children,
  className = '',
  ...props
}: PageHeaderActionsProps) {
  return (
    <div
      className={`col-start-2 row-span-full flex w-full items-center gap-3 md:w-auto md:justify-end ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}
PageHeaderActions.displayName = 'PageHeader.Actions';

type PageHeaderFiltersProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
};

function PageHeaderFilters({
  children,
  className = '',
  ...props
}: PageHeaderFiltersProps) {
  return (
    <div className={`${className}`} {...props}>
      {children}
    </div>
  );
}
PageHeaderFilters.displayName = 'PageHeader.Filters';

const PageHeader = {
  Root: PageHeaderRoot,
  Main: PageHeaderMain,
  TitleGroup: PageHeaderTitleGroup,
  Title: PageHeaderTitle,
  Subtitle: PageHeaderSubtitle,
  Actions: PageHeaderActions,
  Filters: PageHeaderFilters,
};

type ContentProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
};

function Content({ children, className = '', ...props }: ContentProps) {
  return (
    <div className={`flex min-w-0 flex-col gap-6 ${className}`} {...props}>
      {children}
    </div>
  );
}
Content.displayName = 'Page.Content';

export const Page = {
  Root,
  Breadcrumb,
  Content,
};

export { PageHeader };

export type {
  BreadcrumbProps as PageBreadcrumbProps,
  ContentProps as PageContentProps,
  PageHeaderActionsProps,
  PageHeaderFiltersProps,
  PageHeaderMainProps,
  PageHeaderRootProps,
  PageHeaderSubtitleProps,
  PageHeaderTitleGroupProps,
  PageHeaderTitleProps,
  RootProps as PageRootProps,
};
